import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const STAGES = [
  {
    id: "01",
    title: ["Custom Software", "Development"],
    copy: "We design tailored software solutions to enhance your business processes and deliver innovative tools that add value to your operations.",
    tags: "Tailored Systems · Business Tools",
  },
  {
    id: "02",
    title: ["Process Automation", "& Integration"],
    copy: "Accelerate operations and minimize errors with automation systems, seamlessly integrating your existing software for optimal efficiency.",
    tags: "Automation · System Integration",
  },
  {
    id: "03",
    title: ["Digital Transformation", "& Technology Consulting"],
    copy: "We support your digital transformation journey with strategic technology solutions, preparing your business for a future-ready approach.",
    tags: "Strategy · Future-Ready",
  },
];

export default function Technology() {
  const root = useRef(null);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: root.current,
          start: "top top",
          end: "+=340%",
          scrub: 1,
          pin: true,
          anticipatePin: 1,
        },
      });

      tl.fromTo(".tech-bgword", { xPercent: 6 }, { xPercent: -38, duration: 3.4, ease: "none" }, 0);

      tl.fromTo(
        ".tech-stage-1",
        { clipPath: "inset(0 0 0 100%)" },
        { clipPath: "inset(0 0 0 0%)", duration: 0.55, ease: "power3.inOut" },
        0.1
      );
      tl.to(".tech-stage-1", { opacity: 0, x: -80, duration: 0.4, ease: "power2.in" }, 1.0);

      tl.fromTo(
        ".tech-stage-2",
        { clipPath: "inset(100% 0 0 0)" },
        { clipPath: "inset(0% 0 0 0)", duration: 0.55, ease: "power3.inOut" },
        1.15
      );
      tl.to(".tech-stage-2", { opacity: 0, scale: 0.94, duration: 0.4, ease: "power2.in" }, 2.05);

      tl.fromTo(
        ".tech-stage-3",
        { clipPath: "inset(0 100% 0 0)" },
        { clipPath: "inset(0 0% 0 0)", duration: 0.55, ease: "power3.inOut" },
        2.2
      );
      tl.fromTo(".tech-closing", { opacity: 0, y: 24 }, { opacity: 1, y: 0, duration: 0.4 }, 2.75);
      tl.to({}, { duration: 0.45 });
    }, root);
    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={root}
      id="technology"
      className="relative h-[100svh] overflow-hidden"
      data-testid="technology-scene"
      aria-label="Technology — software solutions"
    >
      <span
        className="tech-bgword text-outline-faint pointer-events-none absolute top-1/2 left-0 -translate-y-1/2 whitespace-nowrap font-display text-[26vw] font-black uppercase leading-none"
        aria-hidden="true"
      >
        Software Software
      </span>

      <p className="absolute left-[6vw] top-[14vh] z-20 font-mono text-[10px] uppercase tracking-[0.35em] text-amber md:text-xs">
        05 — Technology
      </p>

      {STAGES.map((s, i) => (
        <div
          key={s.id}
          className={`tech-stage tech-stage-${i + 1} absolute inset-0 z-10 flex flex-col justify-center bg-ink px-[6vw]`}
          data-testid={`tech-stage-${i + 1}`}
        >
          <span
            className="text-outline-faint pointer-events-none absolute right-[6vw] top-[12vh] font-display text-[22vw] font-black leading-none md:text-[16vw]"
            aria-hidden="true"
          >
            {s.id}
          </span>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber md:text-xs">
            Capability {s.id} / 03
          </p>
          <h3 className="mt-5 font-display text-[8vw] font-black uppercase leading-[0.9] tracking-tighter md:text-[5.5vw]">
            {s.title[0]}
            <br />
            {s.title[1]}
          </h3>
          <p className="mt-6 max-w-md text-sm leading-relaxed text-smoke md:text-base">
            {s.copy}
          </p>
          <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-smoke">
            {s.tags}
          </p>
          {i === 2 && (
            <p
              className="tech-closing mt-14 font-display text-2xl font-bold uppercase tracking-tight text-bone md:text-4xl"
              data-testid="tech-closing"
            >
              A trusted partner in both{" "}
              <span className="text-amber">maritime</span> and{" "}
              <span className="text-amber">software</span> industries.
            </p>
          )}
        </div>
      ))}
    </section>
  );
}
